import React, { Fragment, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Table from './Table';
import ApproveModal from './ApproveModal';
import RejectModal from './RejectModal';
import InfoModal from './InfoModal';
import SCTicketModal from './SCTicketModal';
import ShippingModal from './ShippingModal';
import PackingSlipModal from './PackingSlipModal';
import './Orders.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as actions from './Orders.actions';
import * as selectors from './Orders.selectors';
import { stringifyEqualityCheck } from './../../utils';
import * as dialog from './../common/dialog';
import * as loginSelectors from './../Login/Login.selectors';
import { showNotification } from './../../utils';

const Orders = (props) => {
    /* State to props */
    const orders = useSelector(selectors.getOrders, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const idModal = useSelector(selectors.getIdModal);
    const isApproving = useSelector(selectors.getIsApproving);
    const isRejecting = useSelector(selectors.getIsRejecting);
    const type = useSelector(selectors.getType);
    const exportingACMExcel = useSelector(selectors.getExportingACMExcel);
    const exportingUTPExcel = useSelector(selectors.getExportingUTPExcel);
    const exportingOrder = useSelector(selectors.getExportingOrder);
    const pagination = useSelector(selectors.getPagination, stringifyEqualityCheck);
    const sort = useSelector(selectors.getSort, stringifyEqualityCheck);
    const search = useSelector(selectors.getSearch);
    const searchType = useSelector(selectors.getSearchType);
    const isCheckingSCTicket = useSelector(selectors.getIsCheckingSCTicket);
    const isCreatingSCTicket = useSelector(selectors.getIsCreatingSCTicket);
    const ticketExists = useSelector(selectors.getTicketExists);
    const scTicketId = useSelector(selectors.getScTicketId);
    const trackingNumber = useSelector(selectors.getTrackingNumber);
    const shipped = useSelector(selectors.getShipped);
    const isSavingShipping = useSelector(selectors.getIsSavingShipping);
    const shippingMethod = useSelector(selectors.getShippingMethod);
    const shippingQuantity = useSelector(selectors.getShippingQuantity);
    const shippingWeight = useSelector(selectors.getShippingWeight);
    const shippingNumPackages = useSelector(selectors.getShippingNumPackages);
    const packingSlipMessage = useSelector(selectors.getPackingSlipMessage);
    const packingSlipItems = useSelector(selectors.getPackingSlipItems, stringifyEqualityCheck);
    const savingPackingSlip = useSelector(selectors.getSavingPackingSlip);
    const printingPackingSlip = useSelector(selectors.getPrintingPackingSlip);
    const fetchingPackingSlipData = useSelector(selectors.getFetchingPackingSlipData);
    const orderProducts = useSelector(selectors.getOrderProducts, stringifyEqualityCheck);
    const scTicketSelectedItems = useSelector(selectors.getScTicketSelectedItems, stringifyEqualityCheck);
    const generatingPrintReadyFile = useSelector(selectors.getGeneratingPrintReadyFile);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchOrders = useCallback((type) => dispatch(actions.fetchOrders(type)), [dispatch]);
    const setType = useCallback((type) => dispatch(actions.setType(type)), [dispatch]);
    const setIdModal = useCallback((id) => dispatch(actions.setIdModal(id)), [dispatch]);
    const approveOrder = useCallback((history) => dispatch(actions.approveOrder(history)), [dispatch]);
    const rejectOrder = useCallback((history) => dispatch(actions.rejectOrder(history)), [dispatch]);
    const setIsFromEmail = useCallback((value) => dispatch(actions.setIsFromEmail(value)), [dispatch]);
    const setActivePage = useCallback((value) => dispatch(actions.setActivePage(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const exportExcel = useCallback((type, exportType) => dispatch(actions.exportExcel(type, exportType)), [dispatch]);
    const exportOrder = useCallback((id, orderId) => dispatch(actions.exportOrder(id, orderId)), [dispatch]);
    const setSort = useCallback((column, type, orderType) => dispatch(actions.setSort(column, type, orderType)), [dispatch]);
    const setSearch = useCallback((value) => dispatch(actions.setSearch(value)), [dispatch]);
    const setSearchType = useCallback((value) => dispatch(actions.setSearchType(value)), [dispatch]);
    const checkSCTicket = useCallback((id) => dispatch(actions.checkSCTicket(id)), [dispatch]);
    const createSCTicket = useCallback(() => dispatch(actions.createSCTicket()), [dispatch]);
    const setTrackingNumber = useCallback((value) => dispatch(actions.setTrackingNumber(value)), [dispatch]);
    const setShipped = useCallback((value) => dispatch(actions.setShipped(value)), [dispatch]);
    const setShippingMethod = useCallback((value) => dispatch(actions.setShippingMethod(value)), [dispatch]);
    const setShippingQuantity = useCallback((value) => dispatch(actions.setShippingQuantity(value)), [dispatch]);
    const setShippingWeight = useCallback((value) => dispatch(actions.setShippingWeight(value)), [dispatch]);
    const setShippingNumPackages = useCallback((value) => dispatch(actions.setShippingNumPackages(value)), [dispatch]);
    const saveShipping = useCallback(() => dispatch(actions.saveShipping()), [dispatch]);
    const setPackingSlipMessage = useCallback((value) => dispatch(actions.setPackingSlipMessage(value)), [dispatch]);
    const checkPackingSlipItem = useCallback((id, value) => dispatch(actions.checkPackingSlipItem(id, value)), [dispatch]);
    const reorderProduct = useCallback((id, history) => dispatch(actions.reorderProduct(id, history)), [dispatch]);
    const savePackingSlip = useCallback(() => dispatch(actions.savePackingSlip()), [dispatch]);
    const setPackingSlipItems = useCallback((value) => dispatch(actions.setPackingSlipItems(value)), [dispatch]);
    const printPackingSlip = useCallback((id, orderId) => dispatch(actions.printPackingSlip(id, orderId)), [dispatch]);
    const fetchPackingSlipData = useCallback(() => dispatch(actions.fetchPackingSlipData()), [dispatch]);
    const checkScTicketCheckbox = useCallback((order) => dispatch(actions.checkScTicketCheckbox(order)), [dispatch]);
    const generatePrintReadyFile = useCallback((id) => dispatch(actions.generatePrintReadyFile(id)), [dispatch]);
    const showApproveModal = useCallback(() => dispatch(dialog.actions.showDialog('approve-modal')), [dispatch]);
    const hideApproveModal = useCallback(() => dispatch(dialog.actions.hideDialog('approve-modal')), [dispatch]);
    const showRejectModal = useCallback(() => dispatch(dialog.actions.showDialog('reject-modal')), [dispatch]);
    const hideRejectModal = useCallback(() => dispatch(dialog.actions.hideDialog('reject-modal')), [dispatch]);
    const showInfoModal = useCallback(() => dispatch(dialog.actions.showDialog('info-modal')), [dispatch]);
    const hideInfoModal = useCallback(() => dispatch(dialog.actions.hideDialog('info-modal')), [dispatch]);
    const showSCTicketModal = useCallback(() => dispatch(dialog.actions.showDialog('sc-ticket-modal')), [dispatch]);
    const hideSCTicketModal = useCallback(() => dispatch(dialog.actions.hideDialog('sc-ticket-modal')), [dispatch]);
    const showShippingModal = useCallback(() => dispatch(dialog.actions.showDialog('shipping-modal')), [dispatch]);
    const hideShippingModal = useCallback(() => dispatch(dialog.actions.hideDialog('shipping-modal')), [dispatch]);
    const showPackingSlipModal = useCallback(() => dispatch(dialog.actions.showDialog('packing-slip-modal')), [dispatch]);
    const hidePackingSlipModal = useCallback(() => dispatch(dialog.actions.hideDialog('packing-slip-modal')), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        if (props.match.params.type !== 'approved') {
            setSort('uthealth_order_id', 'desc', props.match.params.type);
        } else {
            fetchOrders(props.match.params.type);
        }

        setType(props.match.params.type);
        return () => {
            resetState();
        };
    }, [fetchOrders, setType, resetState, setSort, props.match.params.type]);

    useEffect(() => {
        if (orders.length === 0) {
            return;
        }

        if (props.match.params.action === 'approve') {
            setIdModal(props.match.params.id);
            showApproveModal();
        } else if (props.match.params.action === 'reject') {
            setIdModal(props.match.params.id);
            showRejectModal();
        }
    }, [setIdModal, showApproveModal, showRejectModal, props.match.params.id, props.match.params.action, orders]);

    useEffect(() => {
        setIsFromEmail(typeof props.match.params.id !== 'undefined');
    }, [setIsFromEmail, props.match.params.id]);

    let permissions = [];
    if (user && user.uth_permissions != null) {
        permissions = JSON.parse(user.uth_permissions);
    }

    const ucFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const exportHandler = (exportType) => {
        exportExcel(props.match.params.type, exportType);
    };

    let exportACMButtonLabel = <Fragment><i className="fas fa-cloud-download-alt" /> Export Excel ACM</Fragment>;
    if (exportingACMExcel) {
        exportACMButtonLabel = <i className="fas fa-circle-notch fa-spin" />;
    }

    let exportUTPButtonLabel = <Fragment><i className="fas fa-cloud-download-alt" /> Export Excel UTP</Fragment>;
    if (exportingUTPExcel) {
        exportUTPButtonLabel = <i className="fas fa-circle-notch fa-spin" />;
    }

    const handleSearchUtpCheckOrders = () => {
        setSearch('Customer Treasury Checks');
        setSearchType('product_type');
        fetchOrders(props.match.params.type);
    };

    let exportACMButton = null;
    if (permissions.includes('orders-acm-report')) {
        exportACMButton = <div className="export-acm-button" onClick={exportHandler.bind(this, 1)} disabled={exportingACMExcel}>{exportACMButtonLabel}</div>;
    }

    let exportUTPButton = null;
    if (permissions.includes('orders-utp-report')) {
        exportUTPButton = <div className="export-utp-button" onClick={exportHandler.bind(this, 2)} disabled={exportingUTPExcel}>{exportUTPButtonLabel}</div>;
    }

    let utpCheckOrdersButton = null;
    if (permissions.includes('orders-utp-checks')) {
        utpCheckOrdersButton = <div className="utp-check-orders-button" onClick={handleSearchUtpCheckOrders}><i className="fas fa-money-check-alt" /> UTP Check Orders</div>;
    }

    const handleSearch = () => {
        if (props.search === '') {
            return showNotification('Enter a value', 'Enter a text to search', 'info');
        }

        setActivePage(1);
        fetchOrders(props.match.params.type);
    };

    const keyPressHandler = (event) => {
        if (event.key === 'Enter') {
            if (props.search === '') {
                return showNotification('Enter a value', 'Enter a text to search', 'info');
            }

            setActivePage(1);
            fetchOrders(props.match.params.type);
        }
    };

    const handleShowSCTicketModal = () => {
        if (scTicketSelectedItems.length === 0) {
            return showNotification('Select at least one item', 'Select at least one item to be included in the new SC Ticket', 'info');
        }

        showSCTicketModal();
    };

    let createScTicketButton = null;
    if (type === 'approved' && permissions.includes('orders-sc-ticket')) {
        createScTicketButton = (
            <div className="create-sc-ticket-container">
                <button onClick={handleShowSCTicketModal}>Create SC Ticket</button>
            </div>
        );
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-clock"></i> {ucFirstLetter(props.match.params.type)} orders</Fragment>}
            history={props.history}
        >
            <Helmet><title>{ucFirstLetter(props.match.params.type)} orders - UT Physicians</title></Helmet>
            <div className="orders-container">
                <h2>{ucFirstLetter(props.match.params.type)} orders</h2>
                {exportACMButton}
                {exportUTPButton}
                {utpCheckOrdersButton}
                <div className="search-container">
                    <div class="search-field">
                        <input
                            type="text"
                            placeholder="Search here..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyPress={keyPressHandler}
                        />
                        <select
                            value={searchType}
                            onChange={(event) => setSearchType(event.target.value)}
                        >
                            <option value="uthealth_order_id">By ID</option>
                            <option value="billPo">By PO</option>
                            <option value="product_type">By Product Type</option>
                            <option value="order_type">By Order Type</option>
                            <option value="ordered_by_name">By Ordered By</option>
                            <option value="total_price">By Total</option>
                        </select>
                        <div className="button" onClick={handleSearch}>Search</div>
                    </div>
                </div>
                {createScTicketButton}
                <Table
                    orders={orders}
                    fetching={fetching}
                    type={type}
                    user={user}
                    pagination={pagination}
                    sort={sort}
                    cxmlData={cxmlData}
                    scTicketSelectedItems={scTicketSelectedItems}
                    history={props.history}
                    showApproveModal={showApproveModal}
                    showRejectModal={showRejectModal}
                    showInfoModal={showInfoModal}
                    showSCTicketModal={showSCTicketModal}
                    setIdModal={setIdModal}
                    setActivePage={setActivePage}
                    setSort={setSort}
                    checkSCTicket={checkSCTicket}
                    showShippingModal={showShippingModal}
                    setTrackingNumber={setTrackingNumber}
                    setShipped={setShipped}
                    setShippingMethod={setShippingMethod}
                    setShippingQuantity={setShippingQuantity}
                    setShippingWeight={setShippingWeight}
                    setShippingNumPackages={setShippingNumPackages}
                    reorderProduct={reorderProduct}
                    showPackingSlipModal={showPackingSlipModal}
                    setPackingSlipItems={setPackingSlipItems}
                    setPackingSlipMessage={setPackingSlipMessage}
                    fetchPackingSlipData={fetchPackingSlipData}
                    checkScTicketCheckbox={checkScTicketCheckbox}
                    handleShowSCTicketModal={handleShowSCTicketModal}
                />
            </div>
            <ApproveModal
                idModal={idModal}
                isApproving={isApproving}
                match={props.match}
                history={props.history}
                approveOrder={approveOrder}
                hideApproveModal={hideApproveModal}
                setIdModal={setIdModal}
            />
            <RejectModal
                idModal={idModal}
                isRejecting={isRejecting}
                match={props.match}
                history={props.history}
                rejectOrder={rejectOrder}
                hideRejectModal={hideRejectModal}
                setIdModal={setIdModal}
            />
            <InfoModal
                orders={orders}
                idModal={idModal}
                exportingOrder={exportingOrder}
                user={user}
                generatingPrintReadyFile={generatingPrintReadyFile}
                hideInfoModal={hideInfoModal}
                setIdModal={setIdModal}
                exportOrder={exportOrder}
                generatePrintReadyFile={generatePrintReadyFile}
            />
            <SCTicketModal
                orders={orders}
                idModal={idModal}
                exportingOrder={exportingOrder}
                isCheckingSCTicket={isCheckingSCTicket}
                isCreatingSCTicket={isCreatingSCTicket}
                ticketExists={ticketExists}
                scTicketId={scTicketId}
                scTicketSelectedItems={scTicketSelectedItems}
                hideSCTicketModal={hideSCTicketModal}
                setIdModal={setIdModal}
                checkSCTicket={checkSCTicket}
                createSCTicket={createSCTicket}
            />
            <ShippingModal
                idModal={idModal}
                trackingNumber={trackingNumber}
                shipped={shipped}
                isSavingShipping={isSavingShipping}
                shippingMethod={shippingMethod}
                shippingQuantity={shippingQuantity}
                shippingWeight={shippingWeight}
                shippingNumPackages={shippingNumPackages}
                orders={orders}
                hideShippingModal={hideShippingModal}
                setIdModal={setIdModal}
                setTrackingNumber={setTrackingNumber}
                setShipped={setShipped}
                saveShipping={saveShipping}
                setShippingMethod={setShippingMethod}
                setShippingQuantity={setShippingQuantity}
                setShippingWeight={setShippingWeight}
                setShippingNumPackages={setShippingNumPackages}
            />
            <PackingSlipModal
                orders={orders}
                idModal={idModal}
                trackingNumber={trackingNumber}
                shippingMethod={shippingMethod}
                shippingQuantity={shippingQuantity}
                shippingWeight={shippingWeight}
                shippingNumPackages={shippingNumPackages}
                packingSlipMessage={packingSlipMessage}
                packingSlipItems={packingSlipItems}
                savingPackingSlip={savingPackingSlip}
                printingPackingSlip={printingPackingSlip}
                fetchingPackingSlipData={fetchingPackingSlipData}
                orderProducts={orderProducts}
                setIdModal={setIdModal}
                hidePackingSlipModal={hidePackingSlipModal}
                setTrackingNumber={setTrackingNumber}
                setShippingMethod={setShippingMethod}
                setShippingQuantity={setShippingQuantity}
                setShippingWeight={setShippingWeight}
                setShippingNumPackages={setShippingNumPackages}
                setPackingSlipMessage={setPackingSlipMessage}
                checkPackingSlipItem={checkPackingSlipItem}
                savePackingSlip={savePackingSlip}
                printPackingSlip={printPackingSlip}
            />
        </Layout>
    );
};

export default Orders;
